import { ClickAwayListener, Grid } from '@material-ui/core';
import { Button, Icons, Tooltip, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import React, { Dispatch, SetStateAction, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { Discipline, Site } from '../../models/models';
import type { AssetDetails } from '../../rxdb/assetDetails/assetDetails.types';
import { MobileComponentLocationHeirarchy } from '../../rxdb/inspectionList/queryBuilder';
import { EAM_ID, SAP_ID, YYYYMMDD_DATE_FORMAT } from '../../utilities/constants';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import { getAssetFileType } from '../../utilities/utilityFunctions';
import AssetFileDetails from '../AssetFileDetails';
import useStyles from './AssetProperties.styles';
interface Props {
  assetData: AssetDetails;
  setAssetLocationDialog: Dispatch<SetStateAction<boolean>>;
  setAssetName: Dispatch<SetStateAction<string>>;
  setAssetButton: Dispatch<SetStateAction<string>>;
  inspectionType: string;
  areaListFromRxDB: MobileComponentLocationHeirarchy[];
}

const AssetProperties: React.FC<Props> = ({
  assetData,
  setAssetLocationDialog,
  setAssetName,
  setAssetButton,
  inspectionType,
  areaListFromRxDB,
}) => {
  const size = useWindowDimensions();
  const classes = useStyles({ size });
  const { SiteDiscipline } = useMst();
  const [open, setOpen] = useState<boolean>(false);

  const isVanscoy = SiteDiscipline.siteId === Site.Vanscoy;
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;

  const firstColumnLabels =
    SiteDiscipline.disciplineId === Discipline.Shaft
      ? [
          'Area Type',
          'Area Name',
          'Sub Area Id',
          'Sub Area Layout',
          'Asset Category',
          'Asset Sub Category',
          'AIMS Asset ID',
          'eAM ID',
          'AIMS Asset Name',
          'Asset Description',
          'Date Installed',
          'Floor Level',
          'Floor Movement',
          'Side Walls Movement',
          'Asset Drawings',
        ]
      : [
          isElectrical ? 'Equip/Circuit #' : 'Asset Name',
          'Asset Category',
          'Asset Sub Category',
          isVanscoy ? SAP_ID : EAM_ID,
          'Asset Description',
          'Asset Drawings',
          'Asset Picture',
        ];

  const secondColumnLabels =
    SiteDiscipline.disciplineId === Discipline.Shaft
      ? [
          'Parent Area',
          'Sub Area Location',
          'Lower geological boundary',
          'Upper geological boundary',
          'Temperature',
          'Corrosion',
          'Humidity',
          'Water Make',
          'Comments',
          'Asset Picture',
          'Back Movement',
          'Layout',
        ]
      : [
          isElectrical ? 'PM Type' : 'Inspection level',
          'Asset Location',
          'AIMS Asset ID',
          'Installed Date',
          isElectrical ? 'Manuals' : 'Layout',
          'Comments',
        ];

  const assetFileLabels = [
    'Asset Drawings',
    'Asset Picture',
    'Layout',
    'Manuals',
    'Sub Area Layout',
  ];
  const normalLabels = ['PM Type', 'Inspection level'];
  const labelsWithLineHeight = [
    'AIMS Asset ID',
    'Installed Date',
    'Comments',
    'Parent Area',
    'Lower geological boundary',
    'Upper geological boundary',
    'Temperature',
    'Corrosion',
    'Humidity',
    'Water Make',
    'Back Movement',
  ];
  const assetLocations = ['Asset Location', 'Sub Area Location'];

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const componentLocationChip = (assetIDValues: { name: string; id: string; areaName: string }) => {
    return (
      <Grid
        key={assetIDValues.name}
        className={classes.chipGrid}
        data-testid="location-tree-view"
        onClick={() => {
          localStorage.setItem('id', assetIDValues.id);
          setAssetButton(assetIDValues.id);
          setAssetName(assetIDValues.areaName);
          setAssetLocationDialog(true);
          handleTooltipClose();
        }}
      >
        <Button
          id="assigned"
          color="secondary"
          variant="outlined"
          noMinHeight
          data-cy="assigned"
          className="locationButton"
        >
          <Typography variant="body2" className={classes.assetIdText}>
            {assetIDValues.name}
          </Typography>

          <Icons.InfoFeather
            color="secondary"
            size="small"
            strokeWidth="2"
            className={classes.infoFeather}
          />
        </Button>
      </Grid>
    );
  };

  const getAreaType = (area: string) => {
    const areaTypeTitle = area?.toLowerCase();
    if (areaTypeTitle.includes('sub')) {
      return 'Sub Area';
    } else if (areaTypeTitle.includes('main')) {
      return 'Main Area';
    } else if (areaTypeTitle.includes('elevation')) {
      return 'Elevation';
    }
    return '-';
  };

  const getLabelValue = (label: string) => {
    switch (label) {
      case 'Asset Name':
      case 'Equip/Circuit #':
      case 'Area Name':
        return assetData.assetName || '-';
      case 'Asset Category':
        return assetData.assetMainCategoryName || assetData.assetCategoryName || '-';
      case 'Asset Sub Category':
        return assetData.assetMainCategoryName ? assetData.assetCategoryName || '-' : '-';
      case SAP_ID:
      case EAM_ID:
      case 'eAM ID':
        return SiteDiscipline.disciplineId === Discipline.Shaft
          ? assetData?.eAMAreaId || '-'
          : assetData?.eAMAssetName || '-';
      case 'Asset Description':
        return assetData?.assetDescription || '-';
      case 'PM Type':
      case 'Inspection level':
        return inspectionType;
      case 'AIMS Asset ID':
        return SiteDiscipline.disciplineId === Discipline.Shaft
          ? assetData?.aimsAssetID || '-'
          : assetData?.namePcsId || '-';
      case 'Installed Date':
      case 'Date Installed':
        return assetData?.installationDate?.length > 0
          ? dayjs(assetData?.installationDate).format(`${YYYYMMDD_DATE_FORMAT}`)
          : '-';
      case 'Comments':
        return assetData.comment || '-';
      case 'Area Type':
        return getAreaType(assetData.areaType);
      case 'Sub Area Id':
        return assetData.namePcsId || '-';
      case 'AIMS Asset Name':
        return assetData?.aimsAssetName || '-';
      case 'Floor Level':
        return assetData.floorLevel || '-';
      case 'Parent Area':
        return assetData.parentArea || '-';
      case 'Lower geological boundary':
        return assetData.lowerBound || '-';
      case 'Upper geological boundary':
        return assetData.upperBound || '-';
      case 'Temperature':
        return assetData.temperature || '-';
      case 'Corrosion':
        return assetData.corrosion || '-';
      case 'Humidity':
        return assetData.humidity || '-';
      case 'Water Make':
        return assetData.waterMake || '-';
      case 'Floor Movement':
        return assetData.floorMovement || '-';
      case 'Side Walls Movement':
        return assetData.sideWallMovement || '-';
      case 'Back Movement':
        return assetData.backMovement || '-';
      default:
        return '';
    }
  };

  const getAssetFileView = (label: string) => {
    const files = assetData?.assetFiles?.filter(
      file => file.drawingType === getAssetFileType(label),
    );
    return (
      <Grid container>
        {files.length > 0 ? (
          files.map(file => <AssetFileDetails file={file} key={String(file.fileId)} />)
        ) : (
          <Typography variant="subtitle1" fontWeight="normal" className={classes.staticValues}>
            -
          </Typography>
        )}
      </Grid>
    );
  };

  const getAssetLocationView = () => {
    return (
      <Grid container>
        {areaListFromRxDB.length > 2 ? (
          <ClickAwayListener onClickAway={handleTooltipClose} data-testid="assetlocation">
            <Grid className={classes.displayToolTip}>
              {areaListFromRxDB.map(
                (assetIDValues: MobileComponentLocationHeirarchy, index: number) => {
                  if (index < 2) {
                    return componentLocationChip(assetIDValues);
                  }
                },
              )}
              <Tooltip
                PopperProps={{
                  disablePortal: true,
                }}
                onClose={handleTooltipClose}
                open={open}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title={
                  <Grid className={classes.tooltipLocation}>
                    {areaListFromRxDB.map(
                      (assetIDValues: MobileComponentLocationHeirarchy, index: number) => {
                        if (index > 1) return componentLocationChip(assetIDValues);
                      },
                    )}
                  </Grid>
                }
                placement="bottom"
                arrow
                className={classes.tooltip}
              >
                <Typography
                  color="primary"
                  className={classes.seeMoreText}
                  onClick={handleTooltipOpen}
                >
                  See More
                </Typography>
              </Tooltip>
            </Grid>
          </ClickAwayListener>
        ) : (
          areaListFromRxDB.map((assetIDValues: MobileComponentLocationHeirarchy) => {
            return componentLocationChip(assetIDValues);
          })
        )}
      </Grid>
    );
  };

  return (
    <Grid xs={12} className={classes.mainGrid}>
      <Grid xs={12} className={classes.flexColumn}>
        {firstColumnLabels
          ?.filter(
            label =>
              (label === 'Asset Sub Category' && assetData?.assetMainCategoryName?.length > 0) ||
              SiteDiscipline.disciplineId === Discipline.Shaft ||
              label !== 'Asset Sub Category',
          )
          .map(label => {
            return (
              <Grid
                item
                container
                xs={12}
                className={
                  label === 'Asset Name' || label === 'Equip/Circuit #'
                    ? classes.assetName
                    : classes.entityGrid
                }
                key={label}
              >
                <Grid item xs={12} className={classes.flexRow}>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      color="textSecondary"
                      fontWeight="normal"
                      className={classes.labelStyle}
                    >
                      {label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {!assetFileLabels.includes(label) ? (
                      <Typography
                        variant="subtitle1"
                        fontWeight="normal"
                        className={classes.staticValues}
                        data-testid={`value-${label}`}
                      >
                        {getLabelValue(label)}
                      </Typography>
                    ) : (
                      getAssetFileView(label)
                    )}
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
      </Grid>
      <Grid xs={12} className={classes.flexColumn}>
        {secondColumnLabels.map(label => {
          return (
            <Grid
              item
              container
              xs={12}
              className={`${classes.entityGrid} ${
                (label === 'PM Type' || label === 'Inspection level') && size.height < size.width
                  ? classes.verticalStyleColumn2
                  : ''
              }`}
              key={label}
            >
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    fontWeight="normal"
                    className={classes.labelStyle}
                  >
                    {label}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {normalLabels.includes(label) && (
                    <Typography
                      variant="subtitle1"
                      fontWeight="normal"
                      className={classes.staticValues}
                      data-testid={`value-${label}`}
                    >
                      {getLabelValue(label)}
                    </Typography>
                  )}
                  {labelsWithLineHeight.includes(label) && (
                    <Grid container>
                      <Typography
                        variant="subtitle1"
                        fontWeight="normal"
                        className={classes.assetPropertyText}
                        data-testid={`value-${label}`}
                      >
                        {getLabelValue(label)}
                      </Typography>
                    </Grid>
                  )}
                  {assetFileLabels.includes(label) && getAssetFileView(label)}
                  {assetLocations.includes(label) && getAssetLocationView()}
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default AssetProperties;
